<template>
  <v-layout class="maintenance-history" wrap>
    <v-flex xs12 pb-0>
      <v-layout wrap align-center>
        <v-flex class="d-flex mt-2" xs12 mt-2>
          <h1 class="headline">{{ $t('auto.maintenanceHistory.title') }}</h1>
          <export-button
            v-if="$can('auto_internal_maintenance_export')"
            class="ml-auto"
            large
            action-name="auto/downloadMaintenanceHistory"
            :file-name="$t('auto.maintenanceHistory.title')"
            :query="filters"
          />
        </v-flex>
      </v-layout>
    </v-flex>

    <div class="d-flex search">
      <v-text-field
        v-model="searchString"
        class="maintenance-history__search"
        solo
        hide-details
        :placeholder="$t('auto.fields.numberPlaceholder')"
      />
      <v-btn
        type="button"
        class="search-btn warning"
        depressed
        large
        @click="filters.search = searchString"
      >
        {{ $t('common.btns.search') }}
      </v-btn>
    </div>

    <v-flex class="d-flex" xs12>
      <v-select
        v-model="filters.carParks"
        item-value="id"
        item-text="name"
        class="maintenance-history__filter"
        multiple
        clearable
        :items="parks"
        :label="$t('employees.park')"
        :loading="filtersLoading"
      >
        <template #selection="{ index }">
          <template v-if="index > 0"></template>
          <template v-else>
            {{ filters.carParks.length }}
            {{ $t('common.selected') }}
          </template>
        </template>
      </v-select>

      <v-select
        v-model="filters.suppliers"
        item-value="id"
        item-text="name"
        class="maintenance-history__filter"
        multiple
        clearable
        :items="suppliers"
        :label="$t('auto.internalMaintenance.provider')"
        :loading="filtersLoading"
      >
        <template #selection="{ index }">
          <template v-if="index > 0"></template>
          <template v-else>
            {{ filters.suppliers.length }}
            {{ $t('common.selected') }}
          </template>
        </template>
      </v-select>

      <v-select
        v-model="filters.maintenances"
        item-value="id"
        item-text="name"
        class="maintenance-history__filter"
        multiple
        clearable
        :items="maintenanceTypes"
        :label="$t('auto.internalMaintenance.type')"
        :loading="filtersLoading"
      >
        <template #selection="{ index }">
          <template v-if="index > 0"></template>
          <template v-else>
            {{ filters.maintenances.length }}
            {{ $t('common.selected') }}
          </template>
        </template>
      </v-select>

      <v-menu
        ref="menuDate"
        :close-on-content-click="false"
        v-model="menuDate"
        :return-value.sync="filters.dates"
        max-width="280px"
        min-width="280px"
        class="pr-4 mt-2"
        transition="scale-transition"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            v-on="on"
            v-model="filters.dates"
            :label="$t('auto.replacementDate')"
            readonly
            class="date workers-board__filters-item"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="filters.dates"
          color="primary lighten-1"
          width="280"
          range
          :locale="$i18n.locale"
          :selected-items-text="filters.dates.toString()"
          :allowed-dates="allowedDates"
          @input="changeDate"
        ></v-date-picker>
      </v-menu>
    </v-flex>

    <v-flex xs12 mt-2>
      <v-data-table
        :headers="headers"
        :items="list"
        :items-per-page="perPage"
        :loading="loading"
        hide-default-footer
      >
        <template #body="{ items }">
          <tbody>
            <template v-for="(item, index) in items">
              <tr
                :class="{ updating: itemsUpdating.includes(item.id) }"
                :key="index"
              >
                <td>{{ item.num }}</td>
                <td>{{ item.internal_maintenance.car.number }}</td>
                <td>{{ item.internal_maintenance.car.brand.name }}</td>
                <td>{{ item.internal_maintenance.car.model.name }}</td>
                <td>{{ item.internal_maintenance.car.year }}</td>
                <td>
                  {{
                    item.internal_maintenance.car.park
                      ? item.internal_maintenance.car.park.name
                      : '—'
                  }}
                </td>
                <td>{{ item.type_name }}</td>
                <td class="nowrap">{{ item.change_date }}</td>
                <td>{{ formatAmount(item.tachometer) }}</td>
                <td>
                  <button
                    class="edit-btn"
                    :disabled="
                      !$can('auto_internal_maintenance_edit') ||
                        !$can('auto_internal_maintenance_payment_amount')
                    "
                    @click="
                      () => {
                        editDialog.id = item.id;
                        editDialog.opened = true;
                        editDialog.type = 'number';
                        editDialog.currentValue = Number(item.amount);
                        editDialog.paramName = 'amount';
                      }
                    "
                  >
                    {{ formatAmount(item.amount) }}
                  </button>
                </td>
                <td class="nowrap">
                  <edit-on-click-field
                    :label="item.supplier.name"
                    :disabled="
                      !$can('auto_internal_maintenance_edit') ||
                        !$can('auto_internal_maintenance_supplier')
                    "
                  >
                    <v-select
                      class="maintenance-history__table-select mb-2"
                      item-text="name"
                      item-value="id"
                      dense
                      hide-details
                      :items="suppliers"
                      :value="item.supplier.id"
                      @change="updateItem(item.id, { supplier_id: $event })"
                    />
                  </edit-on-click-field>
                </td>
              </tr>

              <tr v-if="index === items.length - 1" :key="`total-${index}`">
                <td>
                  <b>{{ $t('common.total') }}</b>
                </td>
                <td colspan="7" />
                <td>{{ formatAmount(amountTotal) }}</td>
                <td />
              </tr>
            </template>
          </tbody>
        </template>
        <template #footer>
          <v-divider />
          <div class="pt-1 pb-2">
            <v-pagination
              color="warning"
              :value="currentPage"
              :length="lastPage"
              dark
              circle
              @input="fetchList({ ...filters, page: $event })"
            />
          </div>
        </template>
      </v-data-table>
    </v-flex>

    <text-field-edit-dialog
      v-model="editDialog.opened"
      :type="editDialog.type"
      :current-value="editDialog.currentValue"
      :param-name="editDialog.paramName"
      required
      @save="updateItem(editDialog.id, $event)"
    />

    <v-snackbar v-model="snack.opened" :color="snack.color" bottom right>
      {{ snack.message }}
    </v-snackbar>
  </v-layout>
</template>

<script>
import { mapState } from 'vuex';
import formatAmount from '@/helpers/formatAmount';
import TextFieldEditDialog from '@/components/shared/TextFieldEditDialog.vue';
import EditOnClickField from '@/components/shared/EditOnClickField.vue';
import checkRouteAccess from '@/helpers/checkRouteAccess';
import ExportButton from '@/components/shared/ExportButton.vue';

export default {
  components: { TextFieldEditDialog, EditOnClickField, ExportButton },
  data: () => ({
    loading: false,
    filtersLoading: false,
    menuDate: false,
    itemsUpdating: [],
    searchString: '',

    filters: {
      search: '',
      carParks: [],
      suppliers: [],
      maintenances: [],
      dates: [],
    },

    editDialog: {
      id: '',
      opened: false,
      type: '',
      currentValue: '',
      paramName: '',
    },

    snack: {
      opened: false,
      message: '',
      color: '',
    },
  }),
  watch: {
    filters: {
      immediate: true,
      deep: true,
      handler(params) {
        if (params.dates.length !== 1) {
          this.fetchList(params);
        }
      },
    },
  },
  computed: {
    ...mapState({
      list: ({ auto }) => auto.maintenanceHistory.list,
      currentPage: ({ auto }) => auto.maintenanceHistory.currentPage,
      lastPage: ({ auto }) => auto.maintenanceHistory.lastPage,
      perPage: ({ auto }) => auto.maintenanceHistory.perPage,
      parks: ({ parks }) => parks.list,
      suppliers: ({ suppliers }) => suppliers.list,
      maintenanceTypes: ({ auto }) => auto.maintenanceTypes,
    }),
    headers() {
      return [
        {
          text: this.$t('common.number'),
          sortable: false,
        },
        {
          text: this.$t('auto.fields.number'),
          sortable: false,
        },
        {
          text: this.$t('auto.fields.brand'),
          sortable: false,
        },
        {
          text: this.$t('auto.fields.model'),
          sortable: false,
        },
        {
          text: this.$t('auto.fields.year'),
          sortable: false,
        },
        { text: this.$t('employees.park'), sortable: false },
        { text: this.$t('auto.internalMaintenance.type'), sortable: false },
        { text: this.$t('auto.replacementDate'), sortable: false },
        { text: this.$t('auto.fields.tachometr'), sortable: false },
        { text: this.$t('common.fields.sum'), sortable: false },
        { text: this.$t('auto.internalMaintenance.provider'), sortable: false },
      ];
    },
    amountTotal() {
      return this.list.reduce((sum, item) => (sum += Number(item.amount)), 0); // eslint-disable-line
    },
  },
  methods: {
    formatAmount,
    async fetchList(params) {
      this.loading = true;
      await this.$store.dispatch('auto/fetchMaintenanceHistory', params);
      this.loading = false;
    },
    async loadFilters() {
      this.filtersLoading = true;

      await Promise.all([
        this.$store.dispatch('parks/fetchList'),
        this.$store.dispatch('suppliers/fetchList', { pageRows: 99999 }),
        this.$store.dispatch('auto/fetchMaintenanceTypes'),
      ]);

      this.filtersLoading = false;
    },
    async updateItem(id, params) {
      this.itemsUpdating.push(id);

      const result = await this.$store.dispatch(
        'auto/updateMaintenanceHistoryItem',
        {
          id,
          params,
        },
      );

      this.proccessResult(result);

      this.itemsUpdating = this.itemsUpdating.filter((item) => item !== id);
    },
    proccessResult({ success }) {
      const snack = {
        opened: true,
        message: this.$t('common.saved'),
        color: 'success',
      };

      if (success) {
        this.fetchList({ page: this.currentPage, ...this.filters });
      } else {
        snack.message = this.$t('common.errors.APIerror');
        snack.color = 'error';
      }

      this.snack = snack;
    },
    allowedDates(value) {
      const [from, to] = this.filters.dates;

      if (!from) return true;
      if (to) return true;

      const firstDate = new Date(from);
      const nextDate = new Date(value);

      return nextDate >= firstDate;
    },
    changeDate(val) {
      if (val.length === 2) {
        this.$refs.menuDate.save(this.filters.dates);
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    checkRouteAccess('auto_internal_maintenance_payment_logs', next, to);
  },
  created() {
    this.loadFilters();
  },
};
</script>

<style lang="scss">
.maintenance-history {
  &__search {
    max-width: 400px;
  }

  &__filter {
    max-width: 250px;
    margin-right: 30px;
  }

  &__table-select {
    max-width: 150px;
    .v-select__selection {
      font-size: 13px;
    }
  }

  .search {
    max-width: 400px;
    width: 100%;
    margin: 20px 50px 10px 0;
  }

  .nowrap {
    white-space: nowrap;
  }

  .v-pagination {
    button {
      box-shadow: none !important;
    }
  }

  tr.updating {
    opacity: 0.5;

    * {
      pointer-events: none;
    }
  }

  .edit-btn {
    text-align: left;
    font-size: 14px;
    font-weight: 600;
    color: #464646;
    line-height: 1.3;
    text-decoration: none;

    &[disabled] {
      font-weight: normal;
    }

    &:not([disabled]):hover {
      opacity: 0.7;
    }
  }
}
</style>
